import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { DATE_MODES } from "@/lib/dates";
import { defaultGateTab } from "@nexseed/shared";
import { useAuth } from "@/features/auth/store";
import { visibleGateTabs } from "@/features/gate/labels";
import { GATE_TABS } from "@/features/gate/tabs";
import { GateListPage } from "@/features/gate/components/list/gate-list-page";

// The tab is the URL, like every other list in the app: a guard can bookmark "Inward Pending" and
// a refresh at the window lands them back on the queue rather than on "All". No tab, or one the
// login's roles hide (an old bookmark, a dashboard link), opens the role's own first tab instead.
const search = z.object({
  tab: z.enum(GATE_TABS).optional(),
  q: z.string().optional(),
  // No mode is "All dates", the default: the queue opens with nothing hidden.
  date: z.enum(DATE_MODES).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().int().min(1).default(1),
});

export const Route = createFileRoute("/_app/gate/")({
  validateSearch: search,
  beforeLoad: ({ search: s }) => {
    const auth = useAuth.getState();
    if (!auth.can("gate.view")) throw redirect({ to: "/" });
    const tabs = visibleGateTabs(auth.me?.gateTabs);
    if (!s.tab || !tabs.includes(s.tab))
      throw redirect({
        to: "/gate",
        search: { ...s, tab: defaultGateTab(tabs) },
        replace: true,
      });
  },
  component: Page,
});

function Page() {
  const s = Route.useSearch();
  const navigate = Route.useNavigate();
  // beforeLoad has already sent a missing or hidden tab to the role's first one.
  return (
    <GateListPage
      search={{ ...s, tab: s.tab ?? "inward_pending" }}
      onSearch={(next) =>
        void navigate({ search: (prev) => ({ ...prev, ...next }) })
      }
    />
  );
}
